import type { Template, TemplateThemeMode, Variables } from '@/app/dashboard/templates/components/types/template';
import { fonts } from '@/lib/types';

export const CANONICAL_COLOR_KEYS = [
  'background',
  'surface',
  'primary',
  'secondary',
  'text',
  'muted',
  'accent',
  'divider',
  'promoBg',
  'promoText',
  'promoBorder',
  'availabilityBg',
  'availabilityText',
  'hero',
] as const;

export const PROMOTION_COLOR_KEYS = ['promoBg', 'promoText', 'promoBorder'] as const;
export const STYLE_CANONICAL_COLOR_KEYS = CANONICAL_COLOR_KEYS.filter(
  (key) => !PROMOTION_COLOR_KEYS.includes(key as (typeof PROMOTION_COLOR_KEYS)[number]),
) as (typeof CANONICAL_COLOR_KEYS)[number][];

export const CANONICAL_FONT_KEYS = ['heading', 'body', 'display', 'logo', 'price'] as const;
export const CANONICAL_IMAGE_KEYS = ['logo', 'highlight', 'hero', 'heroTop', 'heroBottom', 'feature', 'cover'] as const;

export const COLOR_LABELS: Record<string, string> = {
  background: 'Background',
  surface: 'Surface',
  primary: 'Primary',
  secondary: 'Secondary',
  text: 'Text',
  muted: 'Muted Text',
  accent: 'Accent',
  divider: 'Dividers',
  promoBg: 'Promo Background',
  promoText: 'Promo Text',
  promoBorder: 'Promo Border',
  availabilityBg: 'Availability Background',
  availabilityText: 'Availability Text',
  hero: 'Hero',
};

export const FONT_LABELS: Record<string, string> = {
  heading: 'Heading Font',
  body: 'Body Font',
  display: 'Display Font',
  logo: 'Logo Font',
  price: 'Price Font',
};

export const IMAGE_LABELS: Record<string, string> = {
  logo: 'Logo Image',
  highlight: 'Highlight Image',
  hero: 'Hero Image',
  heroTop: 'Hero Top Image',
  heroBottom: 'Hero Bottom Image',
  feature: 'Feature Image',
  cover: 'Cover Image',
};

export const COLOR_ALIASES: Record<string, string[]> = {
  background: ['background'],
  surface: ['surface', 'secondary', 'surfaceAlt', 'panel', 'categoryBanner', 'headerBg', 'sectionHeader', 'light'],
  primary: ['primary', 'ink', 'itemTitle', 'headerText', 'footerText', 'categoryText', 'panelText', 'bar', 'heading', 'sectionTitle'],
  secondary: ['secondary', 'surface', 'surfaceAlt', 'panel', 'headerBg'],
  text: ['text', 'foreground', 'ink', 'headerText', 'footerText', 'categoryText', 'panelText', 'light', 'body'],
  muted: ['muted', 'itemDesc', 'badgeText', 'leader', 'subheading', 'mutedForeground'],
  accent: ['accent', 'price', 'discount', 'footerAccent', 'electric', 'hot', 'icon', 'badgeBg', 'headerAccent', 'categoryAccent'],
  divider: ['divider', 'line', 'itemDivider', 'border', 'panelBorder'],
  promoBg: ['promoBg', 'promo'],
  promoText: ['promoText', 'promo', 'discount'],
  promoBorder: ['promoBorder', 'divider', 'line'],
  availabilityBg: ['availabilityBg', 'successBg', 'warnBg'],
  availabilityText: ['availabilityText', 'successText', 'warnText'],
  hero: ['hero'],
};

export const FONT_ALIASES: Record<string, string[]> = {
  heading: ['heading', 'header', 'section', 'category', 'display'],
  body: ['body'],
  display: ['display', 'heading', 'header', 'logo'],
  logo: ['logo', 'display', 'heading', 'header'],
  price: ['price', 'body'],
};

export const IMAGE_ALIASES: Record<string, string[]> = {
  logo: ['logo'],
  highlight: ['highlight', 'feature', 'hero'],
  hero: ['hero', 'feature', 'cover'],
  heroTop: ['heroTop'],
  heroBottom: ['heroBottom'],
  feature: ['feature', 'hero', 'highlight'],
  cover: ['cover', 'hero', 'feature'],
};

export type CanonicalTokensOptions = {
  keepUnknown?: boolean;
  includePromo?: boolean;
};

export type CanonicalTokenEntry = {
  key: string;
  label: string;
  value: string;
};

function formatKeyLabel(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function firstExistingValue(
  source: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return undefined;
}

function normalizeWithAliases(
  input: Record<string, unknown> | undefined,
  canonicalKeys: readonly string[],
  aliasMap: Record<string, string[]>,
  options?: CanonicalTokensOptions,
) {
  const keepUnknown = options?.keepUnknown ?? true;
  const source = { ...(input || {}) };
  const next: Record<string, string> = {};

  if (keepUnknown) {
    Object.entries(source).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim()) {
        next[key] = value;
      }
    });
  }

  for (const canonicalKey of canonicalKeys) {
    const value = firstExistingValue(source, [canonicalKey, ...(aliasMap[canonicalKey] || [])]);
    if (value) {
      next[canonicalKey] = value;
    }
  }

  return next;
}

export function withCanonicalColorTokens(input?: Record<string, unknown>, options?: CanonicalTokensOptions) {
  const canonicalKeys = options?.includePromo === false ? STYLE_CANONICAL_COLOR_KEYS : CANONICAL_COLOR_KEYS;
  return normalizeWithAliases(input, canonicalKeys, COLOR_ALIASES, options);
}

export function withCanonicalFontTokens(input?: Record<string, unknown>, options?: CanonicalTokensOptions) {
  return normalizeWithAliases(input, CANONICAL_FONT_KEYS, FONT_ALIASES, options);
}

export function withCanonicalImageTokens(input?: Record<string, unknown>, options?: CanonicalTokensOptions) {
  return normalizeWithAliases(input, CANONICAL_IMAGE_KEYS, IMAGE_ALIASES, options);
}

export function fontSanitation(fontVal: string): string {
  const f = fontVal.split(',').map(font => font.trim().replace(/'/g, ""));
  const font = fonts.find((font) => font.name.toLocaleLowerCase().includes(f[0].toLocaleLowerCase()));
  return font?.name || f[0];
}

export function toCanonicalTokenEntries(
  tokens: Record<string, unknown> | undefined,
  keyOrder: readonly string[],
  labels?: Record<string, string>,
): CanonicalTokenEntry[] {
  const source = tokens || {};

  const entries = Object.entries(source)
    .filter(([, value]) => typeof value === 'string' && value.trim().length > 0)
    .map(([key, value]) => ({
      key,
      label: labels?.[key] || formatKeyLabel(key),
      value: String(value),
    }));

  const orderIndex = new Map<string, number>();
  keyOrder.forEach((key, index) => orderIndex.set(key, index));

  return entries.sort((a, b) => {
    const aIndex = orderIndex.get(a.key);
    const bIndex = orderIndex.get(b.key);
    if (aIndex !== undefined && bIndex !== undefined) return aIndex - bIndex;
    if (aIndex !== undefined) return -1;
    if (bIndex !== undefined) return 1;
    return a.label.localeCompare(b.label);
  });
}

export function getEditorStyleColorTokens(input?: Record<string, unknown>) {
  return withCanonicalColorTokens(input, {
    keepUnknown: false,
    includePromo: false,
  });
}

export function getEditorPromotionColorTokens(input?: Record<string, unknown>) {
  const all = withCanonicalColorTokens(input, {
    keepUnknown: false,
    includePromo: true,
  });
  const out: Record<string, string> = {};
  PROMOTION_COLOR_KEYS.forEach((key) => {
    const value = all[key];
    if (typeof value === 'string' && value.trim()) {
      out[key] = value;
    }
  });
  return out;
}

export function getEditorFontTokens(input?: Record<string, unknown>) {
  return withCanonicalFontTokens(input, {
    keepUnknown: false,
  });
}

export function getEditorStyleColorEntries(input?: Record<string, unknown>) {
  return toCanonicalTokenEntries(
    getEditorStyleColorTokens(input),
    STYLE_CANONICAL_COLOR_KEYS,
    COLOR_LABELS,
  );
}

export function getEditorFontEntries(input?: Record<string, unknown>) {
  return toCanonicalTokenEntries(
    getEditorFontTokens(input),
    CANONICAL_FONT_KEYS,
    FONT_LABELS,
  );
}

function normalizeVariables(base: Variables | undefined, fallback?: Variables) {
  return {
    ...(base || {}),
    colors: withCanonicalColorTokens({
      ...(fallback?.colors || {}),
      ...(base?.colors || {}),
    }),
    fonts: withCanonicalFontTokens({
      ...(fallback?.fonts || {}),
      ...(base?.fonts || {}),
    }),
    images: withCanonicalImageTokens({
      ...(fallback?.images || {}),
      ...(base?.images || {}),
    }),
  };
}

export function normalizeTemplateVariableStandards(template: Template): Template {
  const cloned = JSON.parse(JSON.stringify(template)) as Template;

  cloned.variables = normalizeVariables(cloned.variables);

  const modes: TemplateThemeMode[] = ['light', 'dark'];
  if (cloned.themeVariants) {
    for (const mode of modes) {
      const variant = cloned.themeVariants[mode];
      if (!variant) continue;

      cloned.themeVariants[mode] = normalizeVariables(variant, cloned.variables);
    }
  }

  return cloned;
}
